import React from 'react';
import { Loader2, WifiOff, AlertTriangle, RefreshCw } from 'lucide-react';
import type { ConnectionStatus as ConnectionStatusType } from '@/types/intelliHealthChat';

interface ConnectionStatusProps {
  status: ConnectionStatusType;
  onRetry?: () => void;
}

const ConnectionStatusBanner: React.FC<ConnectionStatusProps> = ({ status, onRetry }) => {
  if (status === 'connected') return null;

  const isRetryable = status === 'failed' || status === 'auth-failure';

  const config: Record<string, { icon: React.ReactNode; text: string; className: string }> = {
    connecting: {
      icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
      text: 'Connecting...',
      className: 'bg-blue-50 text-blue-700 border-blue-200',
    },
    reconnecting: {
      icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
      text: 'Reconnecting...',
      className: 'bg-amber-50 text-amber-700 border-amber-200',
    },
    disconnected: {
      icon: <WifiOff className="h-3.5 w-3.5" />,
      text: 'Disconnected',
      className: 'bg-red-50 text-red-700 border-red-200',
    },
    'auth-failure': {
      icon: <AlertTriangle className="h-3.5 w-3.5" />,
      text: 'Unable to authenticate chat. Try refreshing.',
      className: 'bg-red-50 text-red-700 border-red-200',
    },
    failed: {
      icon: <WifiOff className="h-3.5 w-3.5" />,
      text: 'Connection failed.',
      className: 'bg-red-50 text-red-700 border-red-200',
    },
  };

  const { icon, text, className } = config[status] || config.disconnected;

  return (
    <div className={`flex items-center gap-2 px-3 py-2 text-xs font-medium border-b ${className}`}>
      {icon}
      <span className="flex-1">{text}</span>
      {isRetryable && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium hover:bg-black/5 transition-colors"
        >
          <RefreshCw className="h-3 w-3" />
          Retry
        </button>
      )}
    </div>
  );
};

export default ConnectionStatusBanner;
